import React, { useState } from 'react';
import { BarChartIcon, TrendingUpIcon, ShoppingCartIcon, UsersIcon, PackageIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, CalendarIcon, InfoIcon } from 'lucide-react';
import { Shop } from '../pages/Shops';
interface ShopDashboardProps {
  shop: Shop;
}
export function ShopDashboard({
  shop
}: ShopDashboardProps) {
  const [hoveredRevenue, setHoveredRevenue] = useState<{
    month: string;
    revenue: number;
  } | null>(null);
  const [hoveredOrder, setHoveredOrder] = useState<{
    month: string;
    orders: number;
  } | null>(null);
  const seasonalPattern = [0.25, 0.3, 0.45, 0.6, 0.75, 0.9, 1.0, 0.95, 0.7, 0.5, 0.35, 0.4];
  // Generate revenue data with more pronounced seasonal patterns
  const revenueData = [{
    month: 'Jan',
    revenue: Math.round(shop.revenue.total * seasonalPattern[0])
  }, {
    month: 'Feb',
    revenue: Math.round(shop.revenue.total * seasonalPattern[1])
  }, {
    month: 'Mar',
    revenue: Math.round(shop.revenue.total * seasonalPattern[2])
  }, {
    month: 'Apr',
    revenue: Math.round(shop.revenue.total * seasonalPattern[3])
  }, {
    month: 'May',
    revenue: Math.round(shop.revenue.total * seasonalPattern[4])
  }, {
    month: 'Jun',
    revenue: Math.round(shop.revenue.total * seasonalPattern[5])
  }, {
    month: 'Jul',
    revenue: Math.round(shop.revenue.total * seasonalPattern[6])
  }, {
    month: 'Aug',
    revenue: Math.round(shop.revenue.total * seasonalPattern[7])
  }, {
    month: 'Sep',
    revenue: Math.round(shop.revenue.total * seasonalPattern[8])
  }, {
    month: 'Oct',
    revenue: Math.round(shop.revenue.total * seasonalPattern[9])
  }, {
    month: 'Nov',
    revenue: Math.round(shop.revenue.total * seasonalPattern[10])
  }, {
    month: 'Dec',
    revenue: Math.round(shop.revenue.total * seasonalPattern[11])
  }];
  // Generate orders data with the same seasonal pattern
  const ordersData = [{
    month: 'Jan',
    orders: Math.round(shop.ordersCount * seasonalPattern[0])
  }, {
    month: 'Feb',
    orders: Math.round(shop.ordersCount * seasonalPattern[1])
  }, {
    month: 'Mar',
    orders: Math.round(shop.ordersCount * seasonalPattern[2])
  }, {
    month: 'Apr',
    orders: Math.round(shop.ordersCount * seasonalPattern[3])
  }, {
    month: 'May',
    orders: Math.round(shop.ordersCount * seasonalPattern[4])
  }, {
    month: 'Jun',
    orders: Math.round(shop.ordersCount * seasonalPattern[5])
  }, {
    month: 'Jul',
    orders: Math.round(shop.ordersCount * seasonalPattern[6])
  }, {
    month: 'Aug',
    orders: Math.round(shop.ordersCount * seasonalPattern[7])
  }, {
    month: 'Sep',
    orders: Math.round(shop.ordersCount * seasonalPattern[8])
  }, {
    month: 'Oct',
    orders: Math.round(shop.ordersCount * seasonalPattern[9])
  }, {
    month: 'Nov',
    orders: Math.round(shop.ordersCount * seasonalPattern[10])
  }, {
    month: 'Dec',
    orders: Math.round(shop.ordersCount * seasonalPattern[11])
  }];
  const topProducts = [{
    name: 'Beach Towel',
    sales: 38,
    revenue: 1139.62
  }, {
    name: 'Sunglasses',
    sales: 27,
    revenue: 1079.73
  }, {
    name: 'Swim Shorts',
    sales: 24,
    revenue: 839.76
  }, {
    name: 'Beach Sandals',
    sales: 22,
    revenue: 549.78
  }];
  const customerStats = {
    total: 287,
    new: 42,
    returning: 245,
    conversionRate: 3.8
  };
  const maxRevenue = Math.max(...revenueData.map(item => item.revenue));
  const maxOrders = Math.max(...ordersData.map(item => item.orders));
  return <div className="space-y-6" data-id="element-4279">
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4280">
        {/* Revenue Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-4281">
          <div className="flex justify-between items-center mb-4" data-id="element-4282">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-4283">
              Revenue Trend
            </h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-4284">
              <CalendarIcon size={14} className="mr-1" data-id="element-4285" />
              Last 12 months
            </div>
          </div>
          <div className="relative h-60 mb-6" data-id="element-4286">
            {hoveredRevenue && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 z-10 shadow-lg" data-id="element-4287">
                {hoveredRevenue.month}: THB{' '}
                {hoveredRevenue.revenue.toLocaleString('en-US')}
              </div>}
            <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2" data-id="element-4288">
              <div data-id="element-4289">THB {maxRevenue.toLocaleString('en-US')}</div>
              <div data-id="element-4290">
                THB {Math.round(maxRevenue * 0.75).toLocaleString('en-US')}
              </div>
              <div data-id="element-4291">
                THB {Math.round(maxRevenue * 0.5).toLocaleString('en-US')}
              </div>
              <div data-id="element-4292">
                THB {Math.round(maxRevenue * 0.25).toLocaleString('en-US')}
              </div>
              <div data-id="element-4293">THB 0</div>
            </div>
            <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-4294">
              <div className="absolute inset-0" data-id="element-4295">
                <div className="border-t border-gray-100 h-1/4" data-id="element-4296"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-4297"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-4298"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-4299"></div>
              </div>
              <div className="relative w-full h-full flex items-end" data-id="element-4300">
                {revenueData.map((item, index) => {
                const barHeight = `${item.revenue / maxRevenue * 100}%`;
                return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredRevenue(item)} onMouseLeave={() => setHoveredRevenue(null)} data-id="element-4301">
                      <div style={{
                    height: barHeight
                  }} className={`w-4/5 ${index === 11 ? 'bg-[#F7941D]' : 'bg-[#F7941D]/70 hover:bg-[#F7941D]'} rounded-t transition-all duration-200`} data-id="element-4302"></div>
                    </div>;
              })}
              </div>
            </div>
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-400 pt-2" data-id="element-4303">
              {revenueData.map((item, index) => <div key={index} className="text-center" data-id="element-4304">
                  {item.month}
                </div>)}
            </div>
          </div>
        </div>

        {/* Orders Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-4305">
          <div className="flex justify-between items-center mb-4" data-id="element-4306">
            <h4 className="text-sm font-semibold text-gray-900" data-id="element-4307">
              Orders Trend
            </h4>
            <div className="flex items-center text-xs text-gray-500" data-id="element-4308">
              <CalendarIcon size={14} className="mr-1" data-id="element-4309" />
              Last 12 months
            </div>
          </div>
          <div className="relative h-60 mb-6" data-id="element-4310">
            {hoveredOrder && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded-lg py-1.5 px-3 z-10 shadow-lg" data-id="element-4311">
                {hoveredOrder.month}: {hoveredOrder.orders.toLocaleString()}{' '}
                orders
              </div>}
            <div className="absolute top-0 left-0 h-full flex flex-col justify-between text-xs text-gray-400 pr-2" data-id="element-4312">
              <div data-id="element-4313">{maxOrders.toLocaleString()}</div>
              <div data-id="element-4314">{Math.round(maxOrders * 0.75).toLocaleString()}</div>
              <div data-id="element-4315">{Math.round(maxOrders * 0.5).toLocaleString()}</div>
              <div data-id="element-4316">{Math.round(maxOrders * 0.25).toLocaleString()}</div>
              <div data-id="element-4317">0</div>
            </div>
            <div className="absolute left-12 right-0 h-full flex items-end" data-id="element-4318">
              <div className="absolute inset-0" data-id="element-4319">
                <div className="border-t border-gray-100 h-1/4" data-id="element-4320"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-4321"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-4322"></div>
                <div className="border-t border-gray-100 h-1/4" data-id="element-4323"></div>
              </div>
              <div className="relative w-full h-full flex items-end" data-id="element-4324">
                {ordersData.map((item, index) => {
                const barHeight = `${item.orders / maxOrders * 100}%`;
                return <div key={index} className="flex-1 flex flex-col items-center justify-end h-full" onMouseEnter={() => setHoveredOrder(item)} onMouseLeave={() => setHoveredOrder(null)} data-id="element-4325">
                      <div style={{
                    height: barHeight
                  }} className={`w-4/5 ${index === 11 ? 'bg-purple-600' : 'bg-purple-400 hover:bg-purple-600'} rounded-t transition-all duration-200`} data-id="element-4326"></div>
                    </div>;
              })}
              </div>
            </div>
            <div className="absolute bottom-0 left-12 right-0 flex justify-between text-xs text-gray-400 pt-2" data-id="element-4327">
              {ordersData.map((item, index) => <div key={index} className="text-center" data-id="element-4328">
                  {item.month}
                </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4329">
        {/* Top Products */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-4330">
          <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-4331">
            Top Selling Products
          </h4>
          <div className="space-y-3" data-id="element-4332">
            {topProducts.map((product, index) => <div key={index} className="flex items-center justify-between border-b border-gray-100 pb-3 last:border-0 last:pb-0" data-id="element-4333">
                <div className="flex items-center" data-id="element-4334">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center text-xs font-semibold text-gray-500 mr-3" data-id="element-4335">
                    {index + 1}
                  </div>
                  <div data-id="element-4336">
                    <p className="text-sm font-medium text-gray-900" data-id="element-4337">
                      {product.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-4338">
                      {product.sales} sold
                    </p>
                  </div>
                </div>
                <div className="text-right" data-id="element-4339">
                  <p className="text-sm font-medium text-gray-900" data-id="element-4340">
                    THB{' '}
                    {product.revenue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-4341">Revenue</p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Customer Insights */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm" data-id="element-4342">
          <h4 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-4343">
            Customer Insights
          </h4>
          <div className="grid grid-cols-2 gap-4" data-id="element-4344">
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-4345">
              <p className="text-xs text-gray-500 mb-1" data-id="element-4346">New Customers</p>
              <p className="text-lg font-bold text-gray-900" data-id="element-4347">
                {customerStats.new}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4348">
                <span className="text-xs text-green-600 flex items-center" data-id="element-4349">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-4350" />
                  14.3%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-4351">
              <p className="text-xs text-gray-500 mb-1" data-id="element-4352">Returning Customers</p>
              <p className="text-lg font-bold text-gray-900" data-id="element-4353">
                {customerStats.returning}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4354">
                <span className="text-xs text-green-600 flex items-center" data-id="element-4355">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-4356" />
                  7.2%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-4357">
              <p className="text-xs text-gray-500 mb-1" data-id="element-4358">Avg. Order Value</p>
              <p className="text-lg font-bold text-gray-900" data-id="element-4359">
                THB{' '}
                {(shop.revenue.total / shop.ordersCount).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
              <div className="mt-1 flex items-center" data-id="element-4360">
                <span className="text-xs text-green-600 flex items-center" data-id="element-4361">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-4362" />
                  3.5%
                </span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4" data-id="element-4363">
              <p className="text-xs text-gray-500 mb-1" data-id="element-4364">Conversion Rate</p>
              <p className="text-lg font-bold text-gray-900" data-id="element-4365">
                {customerStats.conversionRate}%
              </p>
              <div className="mt-1 flex items-center" data-id="element-4366">
                <span className="text-xs text-green-600 flex items-center" data-id="element-4367">
                  <ArrowUpIcon size={10} className="mr-1" data-id="element-4368" />
                  1.2%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}